import type { LineActivity } from './MemberPortalControls';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StaffLineRebindDto, StaffLineRebindResultDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';

export function MemberLineRebindControls({
  memberId,
  onActivity,
  onChanged,
}: {
  memberId: string;
  onActivity?: (activity: LineActivity) => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation('member');
  const allowed = useAuthStore((s) => s.allowedActions?.includes('member:line_rebind'));
  const [preview, setPreview] = useState<StaffLineRebindDto | null>(null);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const operation = useRef('');
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const opener = useRef<HTMLButtonElement>(null);
  const sending = useRef(false);
  const close = () => {
    if (!sending.current) {
      if (onActivity && (reason || checked) && !window.confirm(t('linePanel.discard'))) return;
      setPreview(null);
      setReason('');
      setChecked(false);
      requestAnimationFrame(() => opener.current?.focus());
    }
  };
  const dialog = useDialogFocus(!!preview, close, busy);
  useEffect(() => {
    onActivity?.({ busy, dirty: !!(reason || checked), dialog: !!preview });
  }, [busy, reason, checked, preview, onActivity]);
  if (!allowed || import.meta.env.VITE_MEMBER_PILOT_ENABLED !== 'true') return null;
  async function open() {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.get<StaffLineRebindDto>(`/members/${memberId}/line-rebind`);
      if (!alive.current) return;
      operation.current = crypto.randomUUID();
      setReason('');
      setChecked(false);
      setPreview(result);
    } catch {
      if (!alive.current) return;
      setChecked(false);
      setPreview(null);
      setReason('');
      setMessage(t('lineRebindUnavailable'));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  async function submit() {
    if (sending.current || !preview || !reason.trim() || !checked) return;
    sending.current = true;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.post<StaffLineRebindResultDto>(`/members/${memberId}/line-rebind`, {
        reason: reason.trim(),
        identityChecked: checked,
        expectedVersion: preview.version,
        operationKey: operation.current,
      });
      if (!alive.current) return;
      onChanged?.();
      setPreview(null);
      setReason('');
      setChecked(false);
      setMessage(
        t(
          result.status === 'ACTIVE'
            ? 'lineRebindCompleted'
            : result.status === 'REAUTH_REQUIRED'
              ? 'lineRebindDone'
              : 'lineRebindUnavailable',
          {
            name: result.entryProfile.name,
            url: result.entryProfile.url,
          },
        ),
      );
      requestAnimationFrame(() => opener.current?.focus());
    } catch {
      if (!alive.current) return;
      onChanged?.();
      setChecked(false);
      setPreview(null);
      setReason('');
      setMessage(t('lineRebindRetry'));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  return (
    <section aria-label={t('lineRebindTitle')} className="space-y-3">
      <button
        ref={opener}
        type="button"
        disabled={busy}
        className="rounded-md border border-base-300 px-4 py-2 text-base-content hover:bg-base-200 disabled:opacity-50"
        onClick={() => void open()}
      >
        {t('lineRebindTitle')}
      </button>
      {message && !preview && <p role="status">{message}</p>}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="line-rebind-title"
            tabIndex={-1}
            className="max-h-[90dvh] w-full max-w-lg space-y-4 overflow-auto rounded-lg bg-base-100 p-5"
          >
            <h2 id="line-rebind-title" className="text-lg font-semibold">
              {t('lineRebindTitle')}
            </h2>
            <p>
              {preview.memberNo} · {preview.name} · {preview.phone}
            </p>
            <p>
              {t(
                preview.status === 'REAUTH_REQUIRED'
                  ? 'lineRebindStatusWaiting'
                  : 'lineRebindStatusActive',
              )}
            </p>
            <p className="break-all">
              {t('lineRebindEntry', {
                name: preview.entryProfile.name,
                url: preview.entryProfile.url,
              })}
            </p>
            <p>{t('lineRebindExplanation')}</p>
            <p>{t('lineRebindAccountRisk')}</p>
            <label className="block">
              {t('lineRebindReason')}
              <textarea
                maxLength={500}
                value={reason}
                disabled={busy}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 min-h-24 w-full rounded-md border border-base-300 bg-base-100 px-3 py-2"
              />
            </label>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={checked}
                disabled={busy}
                onChange={(e) => setChecked(e.target.checked)}
              />
              {t('lineRebindChecked')}
            </label>
            {message && <p role="status">{message}</p>}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={close}
                className="rounded-md border border-base-300 px-4 py-2 text-base-content hover:bg-base-200 disabled:opacity-50"
              >
                {t('lineRebindCancel')}
              </button>
              <button
                type="button"
                disabled={busy || !checked || !reason.trim()}
                onClick={() => void submit()}
                className="rounded-md bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
              >
                {t(busy ? 'lineRebindPending' : 'lineRebindSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
